import React from "react";
import "../../../assets/stylesheets/gip-login-modal.css";
import "../../../assets/stylesheets/clinician-onboard-wizard.css";
import { getFirebaseUser } from "../../../services/firebase";
import ApiService from "../../../services/ApiService";
import {
  LOG_SEVERITIES,
  MESSAGES_TYPES,
  USER_TYPES,
} from "../../../assets/appConstants";
import { postMessage } from "../../../services/utils";
import SystemPromptService from "../../../services/systemPromptService";
import { ALERTS } from "../../common/systemAlert";
import SpinnerMan from "../../common/spinner-man";
import RegistrationWizardState from "./registrationWizardState";
import { mapTherapistMeToWizardAccount } from "./registrationWizardBootstrap";
import { bootstrapClinicianRegistration } from "./registrationWizardInviteFlow";
import {
  DEMO_THERAPIST_ME_NEW_USER,
  DEMO_THERAPIST_ME_SUB_ACCOUNT,
  REGISTRATION_SCENARIOS,
} from "./registrationWizardConstants";
import WizardTopbar from "./components/WizardTopbar";
import WizardStepper from "./components/WizardStepper";
import WizardFooter from "./components/WizardFooter";
import WelcomeIntroStep from "./steps/WelcomeIntroStep";
import WelcomeStep from "./steps/WelcomeStep";
import AccountSecurityStep from "./steps/AccountSecurityStep";
import { WIZARD_STEP_IDS } from "./registrationWizardSteps";
import ClinicalProfileStep from "./steps/ClinicalProfileStep";
import ClinicSettingsStep from "./steps/ClinicSettingsStep";
import PhotoPhoneStep from "./steps/PhotoPhoneStep";
import AboutYouStep from "./steps/AboutYouStep";
import SuccessStep from "./steps/SuccessStep";
import ClinicianRegistrationScreen from "./components/ClinicianRegistrationScreen";
import { isV2SaveSuccess } from "./registrationWizardApi";

const getDemoMePayload = (demoScenario) =>
  demoScenario === REGISTRATION_SCENARIOS.NEW_USER
    ? DEMO_THERAPIST_ME_NEW_USER
    : DEMO_THERAPIST_ME_SUB_ACCOUNT;

export const CLINICIAN_ONBOARDING_DEMO_ACCOUNT = {
  ...DEMO_THERAPIST_ME_SUB_ACCOUNT,
  accountId: "demo-clinician-sub-account",
};

class ClinicianRegistrationWizard extends React.Component {
  constructor(props) {
    super(props);
    postMessage(MESSAGES_TYPES.GO_ONBOARDING);

    let accountObj = null;
    if (props.demo) {
      const demoScenario =
        props.demoScenario === REGISTRATION_SCENARIOS.NEW_USER
          ? REGISTRATION_SCENARIOS.NEW_USER
          : REGISTRATION_SCENARIOS.SUB_ACCOUNT;
      const demoMe = getDemoMePayload(demoScenario);
      accountObj = {
        ...mapTherapistMeToWizardAccount(demoMe, `demo-clinician-${demoScenario}`, {
          registrationScenario: demoScenario,
        }),
        accountType: USER_TYPES.THERAPIST,
        needToSetPassword: true,
      };
      RegistrationWizardState.init(accountObj, { fresh: true });
      RegistrationWizardState.finalizeStepPipeline({ demo: true });
    }

    this.callLoginHelper = !props.demo;
    this.state = {
      stepIndex: RegistrationWizardState.getStepIndex(),
      showValidation: false,
      accountObj: props.demo ? RegistrationWizardState.getAccount() : accountObj,
      toast: null,
      saving: false,
    };

    this._onChange = this._onChange.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.goBack = this.goBack.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    if (this.props.demo) {
      RegistrationWizardState.addChangeListener(this._onChange);
      this.setState({
        accountObj: RegistrationWizardState.getAccount(),
        stepIndex: RegistrationWizardState.getStepIndex(),
      });
      document.addEventListener("keydown", this.handleKeyDown);
      return;
    }

    if (this.callLoginHelper) {
      this.callLoginHelper = false;
      const { prefetchedRegistration, prefetchedLoginRes, prefetchedAccountId } = this.props;

      if (prefetchedRegistration) {
        const accountObj = {
          ...prefetchedRegistration,
          accountType: prefetchedRegistration.accountType || USER_TYPES.THERAPIST,
        };
        RegistrationWizardState.init(accountObj, { fresh: true, fromApi: true });
        RegistrationWizardState.finalizeStepPipeline()
          .then(() => {
            postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
            RegistrationWizardState.addChangeListener(this._onChange);
            this.setState({
              accountObj,
              stepIndex: RegistrationWizardState.getStepIndex(),
            });
          });
        document.addEventListener("keydown", this.handleKeyDown);
        return;
      }

      const initWizard = (accountId, loginRes) => {
        bootstrapClinicianRegistration(accountId, { loginRes })
          .then((bootstrap) => {
            const accountObj = bootstrap.wizardAccount;
            RegistrationWizardState.init(accountObj, { fresh: true, fromApi: true });
            return RegistrationWizardState.finalizeStepPipeline().then(() => accountObj);
          })
          .then((accountObj) => {
            postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
            RegistrationWizardState.addChangeListener(this._onChange);
            this.setState({
              accountObj,
              stepIndex: RegistrationWizardState.getStepIndex(),
            });
          })
          .catch(() => {
            ApiService.logIt(
              "clinician registration bootstrap failed",
              LOG_SEVERITIES.ALERT,
              loginRes
            );
            SystemPromptService.showAlert(ALERTS.INVALID_OTL_CODE);
          });
      };

      if (prefetchedLoginRes && prefetchedAccountId) {
        initWizard(prefetchedAccountId, prefetchedLoginRes);
      } else {
        getFirebaseUser(this.props.customToken).then((user) => {
          initWizard(user.uid, {
            accountId: user.uid,
            customToken: this.props.customToken,
          });
        });
      }
    }
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.stepIndex === 1 && prevState.stepIndex !== 1) {
      RegistrationWizardState.applyGeoLanguageDefaults();
    }
  }

  componentWillUnmount() {
    RegistrationWizardState.removeChangeListener(this._onChange);
    document.removeEventListener("keydown", this.handleKeyDown);
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
    }
  }

  _onChange() {
    this.setState({ stepIndex: RegistrationWizardState.getStepIndex() });
  }

  handleKeyDown(e) {
    if (e.code === "Enter" && !this.state.saving) {
      this.nextStep();
    }
  }

  showToast(message, { variant = "success" } = {}) {
    this.setState({ toast: { message, variant } });
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => this.setState({ toast: null }), 3200);
  }

  goBack() {
    RegistrationWizardState.previousStep();
    this.setState({ showValidation: false, stepSaveError: null });
  }

  clearStepSaveError = () => {
    if (this.state.stepSaveError) {
      this.setState({ stepSaveError: null });
    }
  };

  nextStep() {
    const { stepIndex, saving } = this.state;
    if (saving || RegistrationWizardState.isSuccessStep()) return;

    if (!RegistrationWizardState.validateCurrentStep()) {
      this.setState({ showValidation: true });
      RegistrationWizardState.emitChange();
      return;
    }

    this.setState({ showValidation: false, saving: true });

    const currentStepId = RegistrationWizardState.getCurrentStepId();

    RegistrationWizardState.saveStep(currentStepId, { demo: this.props.demo })
      .then((result) => {
        const success =
          result === true ||
          result?.success === true ||
          (isV2SaveSuccess(result) && result?.success !== false);
        const saveError = typeof result === "object" ? result?.error : null;

        if (!success) {
          this.setState({
            saving: false,
            showValidation: true,
            stepSaveError: saveError,
          });
          if (!saveError) {
            this.showToast("Could not save. Please check your entries and try again.", {
              variant: "error",
            });
          }
          return;
        }

        if (currentStepId === WIZARD_STEP_IDS.CLINICAL) {
          this.showToast(RegistrationWizardState.getClinicalStepToastMessage(), {
            variant: "success",
          });
        }

        if (stepIndex === RegistrationWizardState.getTotalSteps() - 1) {
          RegistrationWizardState.goToSuccess();
        } else {
          RegistrationWizardState.nextStep();
        }
        this.setState({ saving: false, stepSaveError: null });
      })
      .catch(() => {
        this.setState({ saving: false });
      });
  }

  renderStep() {
    const { showValidation, stepSaveError } = this.state;
    const stepId = RegistrationWizardState.getCurrentStepId();

    switch (stepId) {
      case WIZARD_STEP_IDS.WELCOME_INTRO:
        return <WelcomeIntroStep />;
      case WIZARD_STEP_IDS.NAME:
        return <WelcomeStep showValidation={showValidation} />;
      case WIZARD_STEP_IDS.SECURITY:
        return <AccountSecurityStep />;
      case WIZARD_STEP_IDS.CLINICAL:
        return <ClinicalProfileStep showValidation={showValidation} />;
      case WIZARD_STEP_IDS.CLINIC:
        return <ClinicSettingsStep />;
      case WIZARD_STEP_IDS.PHOTO:
        return (
          <PhotoPhoneStep
            showValidation={showValidation}
            saveError={stepSaveError}
            onSaveErrorClear={this.clearStepSaveError}
          />
        );
      case WIZARD_STEP_IDS.ABOUT:
        return <AboutYouStep showValidation={showValidation} />;
      default:
        return null;
    }
  }

  renderWizardContent() {
    const { stepIndex, toast, saving, accountObj } = this.state;

    if (!accountObj) {
      return <SpinnerMan />;
    }

    const isSuccess = RegistrationWizardState.isSuccessStep();
    const isNextDisabled = RegistrationWizardState.isNextDisabled();
    const currentStepId = !isSuccess ? RegistrationWizardState.getCurrentStepId() : null;
    const isWelcomeIntro = currentStepId === WIZARD_STEP_IDS.WELCOME_INTRO;
    const isPhoneStep = currentStepId === WIZARD_STEP_IDS.PHOTO;

    return (
      <div className="clinician-onboard-shell">
        <div className="clinician-onboard-card">
          {!isSuccess && <WizardStepper stepIndex={stepIndex} />}
          {!isSuccess && <WizardTopbar />}

          <div
            className={`clinician-onboard-body${
              isWelcomeIntro ? " clinician-onboard-body--welcome-intro" : ""
            }${isPhoneStep ? " clinician-onboard-body--phone" : ""}${
              isSuccess ? " clinician-onboard-body--success" : ""
            }`}
          >
            {isSuccess ? this.renderSuccess() : this.renderStep()}
          </div>

          {toast && (
            <div
              className={`clinician-onboard-toast clinician-onboard-toast--${toast.variant || "success"}`}
              role="status"
              aria-live="polite"
            >
              <span className="clinician-onboard-toast__icon" aria-hidden="true">
                {toast.variant === "error" ? "!" : "✓"}
              </span>
              <div className="clinician-onboard-toast__content">
                <p className="clinician-onboard-toast__title">{toast.message || toast}</p>
                {toast.variant !== "error" && (
                  <p className="clinician-onboard-toast__subtitle">
                    You can continue setting up your profile
                  </p>
                )}
              </div>
            </div>
          )}

          {!isSuccess && (
            <WizardFooter
              isNextDisabled={isNextDisabled}
              isFirstStep={stepIndex === 0}
              isSubmitStep={stepIndex === RegistrationWizardState.getTotalSteps() - 1}
              saving={saving}
              onBack={this.goBack}
              onNext={this.nextStep}
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    return (
      <ClinicianRegistrationScreen>{this.renderWizardContent()}</ClinicianRegistrationScreen>
    );
  }

  renderSuccess() {
    return <SuccessStep demo={this.props.demo} />;
  }
}

export default ClinicianRegistrationWizard;
